/* eslint-disable @altermaind/you-shall-not-pass */
import { EMAIL_SUPPORT } from '@/_shared/constants/general';
import {
  SubscriptionIntervalType,
  SubscriptionPaymentMethod,
  SubscriptionPlanType,
} from '@/billing/state';
import { ClusterType } from '@/swagger/constants/credits-enums';

export const en = {
  settings: {
    pageTitle: 'Settings',
    tabs: {
      plan: {
        title: 'Plan',
        companyName: 'Company name',
        companyVatNumber: 'VAT Number',
        companyAlert: 'Do you want to edit company data?',
        companyAlertLink: 'Contact us',
        companyAlertSuffix: 'to update your company information.',
        noActivePlan: {
          title: 'No active plan',
          description:
            'There was a problem activating your plan. We are working to resolve it.\nFor assistance or information, write to us at slash@altermaind.com',
        },
        yourPlan: {
          title: 'Your Plan',
          statusActive: 'Subscription active',
          statusTrial: {
            one: '{{count}}-day trial active',
            other: '{{count}}-day trial active',
          },
          learningLicenses: 'Learning licenses',
          learningLicensesTooltip:
            'A learning license grants platform access to every employee who will be assigned an assessment. It is activated when the recipient receives their first assessment. You can manage active licenses from the Records section.',
          adminLicenses: 'Admin licenses',
          adminLicensesTooltip:
            'The admin license corresponds to an admin or delegate user who does not operate on the learning dashboard. This type of license does not contribute to Slash billing.',
          trialMonthlyPrice: '{{price}}',
          monthlyPricePerLicensePrice: '{{price}}/',
          monthlyPricePerLicenseSuffix: ' month per learning license',
          trialPriceSubtitle: {
            days: {
              one: 'Free for the first day, then {{price}}/ month per learning license.',
              other:
                'Free for the first {{count}} days, then {{price}}/ month per learning license.',
            },
            months: {
              one: 'Free for the first month, then {{price}}/ month per learning license.',
              other:
                'Free for the first {{count}} months, then {{price}}/ month per learning license.',
            },
          },
          pricesVatExcluded: 'The price is VAT excluded',
          offlineReviewOffer: 'If you want to review the offer',
          offlineReviewOfferCta: 'Book a consultation',
          offlineContractDetails:
            'Further details on payment and included services are reported in the contract.',
          learningLimitExceededAlert:
            'You have over {{max}} active learning licenses. Consider moving to a Custom plan to reduce costs.',
          learningLimitExceededCta: 'Book a consultation',
        },
        planTitles: {
          STANDARD: 'Your Plan',
          CUSTOM: 'Your Plan',
          CUSTOM_OFFLINE: 'Your Plan',
        } satisfies Record<SubscriptionPlanType, string>,
        billingInterval: {
          DAY: { one: 'Every day', other: 'Every {{count}} days' },
          WEEK: { one: 'Every week', other: 'Every {{count}} weeks' },
          MONTH: { one: 'Every month', other: 'Every {{count}} months' },
          YEAR: { one: 'Every year', other: 'Every {{count}} years' },
        } satisfies Record<SubscriptionIntervalType, { one: string; other: string }>,
        paymentMethods: {
          CARD: 'Card',
          SEPA_DEBIT: 'SEPA Direct Debit',
        } satisfies Record<SubscriptionPaymentMethod, string>,
        details: {
          title: 'Plan details',
          activationDate: 'Activation date:',
          billingType: 'Billing type:',
          paymentMethod: 'Payment method:',
          nextInvoice: 'Next invoice:',
          managePlanCta: 'Manage plan and payments',
          customNeeds:
            'Do you have specific needs in volume, configuration or services beyond your plan?',
          customNeedsCta: 'Book a consultation',
          customNeedsSuffix: 'to get a tailored plan',
        },
        included: {
          title: 'INCLUDED IN THE PLAN:',
          measurement: {
            title: 'MEASUREMENT',
            measurementSoftSkills: 'Soft skills assessment every 12 months',
            measurementHardSkills: 'Hard skills assessment every 6 months',
            measurementOnDemand: 'On-demand assessment on request',
          },
          coaching: {
            title: 'RESULTS COACHING',
            coachingHr: '1 consultation for the HR team every 12 months',
            coachingOneToOne:
              'Up to 20 one-to-one consultations for your employees with the Slash team',
          },
          training: {
            title: 'TRAINING',
            trainingCatalog:
              'Free continuously updated course catalog (videos, quizzes and guides)',
            trainingCustom: 'Custom training based on pain points',
          },
          aiBoost: {
            title: 'AI-BOOST',
            aiBoostFeatures: 'AI assistant on features and the model',
          },
        },
      },
      delegates: {
        title: 'Admins and Delegates',
        admin: {
          title: 'HR Admin',
          firstName: 'Admin First Name',
          email: 'Admin Email',
        },
        filter: 'Filters',
        search: {
          placeholder: 'Search',
        },
        listTitle: 'Delegates List',
        searchDelegates: 'Search delegates',
        resendInvite: {
          dialog: {
            title: 'Resend delegation',
            description: 'You are about to resend the invitation to {{fullName}}.',
            confirmation: 'Are you sure you want to proceed?',
            buttons: {
              confirm: 'Resend',
              cancel: 'Cancel',
            },
            success: 'Invitation successfully sent',
          },
        },
        filterDelegates: {
          status: {
            label: 'Status',
          },
          buttons: {
            save: 'Save',
            cancel: 'Reset',
          },
        },
        removeDelegate: {
          dialog: {
            title: 'Remove Delegate',
            description:
              'You are removing a delegate. Once removed, the delegate will no longer be able to access the Admin dashboard.',
            confirmation: 'Are you sure you want to proceed?',
            buttons: {
              confirm: 'Remove',
              cancel: 'Cancel',
            },
          },
        },
        addDelegate: {
          emptyState: {
            title: 'Delegates list',
            description:
              'No delegates are yet associated with the Organization. Click “Add Delegate” below to add the first one.',
            alert:
              'Organization Delegates have the same permissions as the Admin, except for inviting other Delegates. For more information, check the !{DOCS}! or !{CONTACT}!',
            labelDocs: 'guide',
            labelContact: 'contact us',
            button: 'Add Delegate',
          },
          dialog: {
            title: 'Add Delegate',
            chooseTeamsOrGuest: {
              description:
                'A delegate can help you manage records, assessments, analysis, and reporting. We suggest choosing someone from the department that usually handles people management, but you can select anyone you consider suitable.',
              listOfTeamsLabel: 'Team:',
              addDelegateGuestQuestion:
                'Would you like to delegate a new user who is not yet registered on Slash?',
              addDelegateGuestCta: 'Add',
              noDepartmentsFound: 'No departments found',
              unknownDepartment: 'Not assigned',
            },
            delegateEmployees: {
              description: 'Here are the people who can support you in daily management tasks.',
              noResults: 'No profiles found in this department',
              table: {
                people: 'people',
                name: 'Name',
                role: 'Role',
                email: 'Email',
              },
              cta: {
                goBack: 'Back',
                next: 'Next',
              },
              confirm: {
                title: 'Confirm Delegates',
                description: 'Here is the delegate summary',
                department: 'Department',
                role: 'Role',
                email: 'Email',
                cta: {
                  confirm: 'Add',
                  changeSelection: 'Edit selection',
                },
                infoBadge: 'The delegate will receive a notification email for access',
              },
            },
            fields: {
              firstName: 'Delegate First Name',
              lastName: 'Delegate Last Name',
              email: 'Delegate Email',
              selectedEmployee: 'Search by name and surname',
            },
            alertInvalidEmail: 'The email format is invalid. Please check and try again.',
            step0: {
              descriptionLine1:
                'A delegate can help you manage records, questionnaires, analysis, and reporting.',
              descriptionLine2:
                ' We suggest choosing someone from the HR team, but you can select anyone you consider suitable.',
              titleCard: 'Choose from all employees',
              peopleLabel: 'people',
              descriptionNoPeople:
                'Would you like to delegate a new user who is not yet registered on Slash?',
            },
            step2: {
              fields: {
                firstName: 'Name',
                role: 'Role',
                email: 'Email',
              },
              recap: 'Summary: {{count}} person will be added as a delegate',
              description:
                'Select the person you want to nominate as delegate from all company employees',
              alert:
                'You are selecting outside the HR department. Make sure the selected person is familiar with HR processes and company policies.',
              all: 'All employees',
            },
            buttons: {
              next: 'Next',
              save: 'Add',
              close: 'Cancel',
              back: 'Back',
              addNewUser: 'Add a delegate',
            },
          },
        },
        table: {
          firstName: 'Delegate First Name',
          lastName: 'Delegate Last Name',
          email: 'Delegate Email',
          status: {
            label: 'Delegate status',
          },
          tooltips: {
            remove: 'Remove delegate',
            resendInvite: 'Resend invite',
          },
        },
      },
      credits: {
        title: 'Credits',
        noData: {
          title: 'Credits',
          description: 'No data available yet: send the assessment to start tracking credit usage.',
        },
        recap: {
          title: 'Credits Analysis',
          nextRecharge: 'Credit renewal: !{DATE}! ({{days}} days remaining)',
          description:
            'The data only considers profiles invited to register on Slash (including active users). Profiles uploaded but never invited are excluded.',
        },
        progress: {
          total: 'Credits spent in the selected period: ',
          overall: 'Overall credit usage',
          overallTooltip:
            'The percentage indicates the share of credits used compared to the total allocation available for the organization. Since credits accumulate for three months, the actual monthly usage may exceed 100%, even though displayed values will never go beyond that threshold.',
          media: 'Average monthly usage: ',
        },
        cluster: {
          labelPerson: 'people in {{clusterName}}',
          title: 'Credit Usage Detail',
          description:
            'Each range shows the average level of credit usage by users in the organization. The distribution helps identify who fully uses their available credits and who has room for improvement.',
          table: {
            cluster: 'Cluster',
            clusterTooltip:
              'Clusters are calculated based on total credit consumption in the selected period',
            media: 'Average credits',
            mediaLabel: 'credits/month',
            person: 'People',
            personLabel: 'people',
          },
          [ClusterType.INATTIVI]: {
            label: 'Inactive',
            subLabel: '0%',
          },
          [ClusterType.SOTTO_UTILIZZATORI]: {
            label: 'Under-users',
            subLabel: '1-29%',
          },
          [ClusterType.OCCASIONALI]: {
            label: 'Occasional',
            subLabel: '30-49%',
          },
          [ClusterType.MODERATI]: {
            label: 'Moderate',
            subLabel: '50-69%',
          },
          [ClusterType.REGOLARI]: {
            label: 'Regular',
            subLabel: '70-89%',
          },
          [ClusterType.INTENSIVI]: {
            label: 'Intensive',
            subLabel: '90-100%',
          },
        },
        department: {
          title: 'Credit usage by department',
          showMore: 'Show more',
          showLess: 'Show less',
          alertLabel:
            'Would you like more in-depth analysis? !{LINK}!, and explore the department of interest',
          alertLinkLabel: 'go to Departments',
          description:
            'Details of credit usage by each company department, indicating the average consumption per person and the portion used compared to the allocation for the selected period.',
          buttonCta: 'View credits',
          creditPerPerson: 'Average consumption per person: !{TOTAL}! credits',
        },
        trend: {
          title: 'Average consumption and trend',
          description:
            'Average and trend of consumption at company level during the selected period.',
          chartTitle: 'Credit usage trend',
          averageMonthlyUsage: 'Average monthly usage',
          averageMonthlyUsageTooltip:
            'Indicates the average number of credits spent per employee in the selected period. For multi-month periods, the value is already normalized on a monthly basis, so it can be compared to individual months.',
          creditLabel: 'credits per person',
          creditsLabel: 'credits',
          trendLabel: 'Quarterly trend',
          trendLabelTooltip:
            'Shows the percentage (and credit) variation of average usage per person compared to the previous period of the same type (month or quarter). It helps identify growth or decline trends.',
        },
      },
      aspect: {
        tabTitle: 'Visual identity',
        title: 'Customize the look of your Slash platform',
        description:
          'Personalize the appearance of your platform with your brand. Enter your company website and let the AI do the work, or upload your logos and choose your preferred color.',
        siteLabel: 'Your company website',
        sitePlaceholder: 'https://',
        siteDescription:
          '{{remainingDesignQuota}}/{{totalDesignQuota}} attempts available to upload the logo from the provided website',
        alert:
          'For the best experience, we recommend uploading both light and dark versions of your logo. If you upload only one, the missing version will be generated by AI.',
        remainingDesignQuotaZeroAlert:
          'You have no remaining attempts to preview. You can try uploading the logos manually.',
        alertError:
          'Unable to retrieve logos from the provided URL. Try a different link or upload them manually.',
        alertErrorColors:
          'Unable to retrieve theme colors. Try a different link or select them manually.',
        cta: {
          generate: 'Generate',
          save: 'Save',
        },
        loading: {
          title: 'AI is analyzing the provided page',
          description: 'The logos will soon appear in the preview below.',
        },
        errors: {
          designQuotaExceeded:
            'You have exceeded the number of available attempts to upload the logo from a website. You can upload it manually or contact support to upgrade your plan.',
        },
        uploadLogo: {
          titleWhiteMode: 'Company logo White mode',
          titleTooltipWhiteMode: 'Dark version of the logo, ideal for light backgrounds.',
          titleDarkMode: 'Company logo Dark mode',
          titleTooltipDarkMode: 'Light version of the logo, ideal for dark or colored backgrounds.',
        },
        titleColor: 'Theme color',
        previewLabel: 'Platform preview',
        previewLabelTooltip:
          'Here you can see a preview of the platform in dark and light mode. The theme choice is personal and can be managed by each user directly from the dropdown in the top-right corner.',
      },
    },
    tutorial: {
      title: 'Manage your settings',
      slides: {
        slide1: {
          title: 'Check your plan information',
          description:
            'Keep track of the licenses used, monitor monthly costs per license if applicable to your plan, manage your payment method, and view your billing history.',
        },
        slide2: {
          title: 'View your company data',
          description: `In this section you can view your company’s main information, such as name and VAT number. These details cannot be edited directly: if you need updates, contact us at ${EMAIL_SUPPORT}`,
        },
        slide3: {
          title: 'Manage Admins and Delegates',
          description:
            'You can view the main Admin’s data and add or manage Delegates. Delegates have the same permissions as the Admin, with one exception: they cannot invite other Delegates. This allows an Admin to share platform management without losing full control.',
        },
        slide4: {
          title: 'Monitor credit usage',
          description:
            'Access the dashboard to view the complete credit consumption analysis. Track company and departmental usage over time.',
        },
        slide5: {
          title: 'Customize Slash for your company',
          description:
            'Upload your company logo or enter your website URL to let the AI detect it automatically. Choose your preferred platform color and preview how Slash will appear in both dark and light modes.',
        },
      },
    },
  },
};
